/**
 * Add Bot dialog — mints an enroll code, shows the copy-paste one-liner,
 * then polls until the new bot appears in the fleet list and auto-closes.
 *
 * Sequence:
 *   1. operator opens dialog, enters optional label, clicks Mint
 *   2. POST /api/bots/enroll-codes returns { code, oneLiner, expiresAt }
 *   3. dialog shows the one-liner + a live countdown to TTL expiry
 *   4. parent polls listBots() every 5s; once a new bot with
 *      registered_at > dialogOpenedAt appears, the parent closes the
 *      dialog automatically.
 */

import { useEffect, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Copy, Loader2 } from "@/components/ui/icons";
import { mintEnrollCode, type MintedEnrollCode } from "@/lib/api/bots";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional — parent passes a hint the dialog has provisioned a bot so
   *  it can close itself. The parent does the listBots() polling and
   *  triggers this on match. */
  newBotRegistered: boolean;
}

function countdown(expiresAt: number): string {
  const ms = expiresAt - Date.now();
  if (ms <= 0) return "expired";
  const min = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

export function AddBotDialog({ open, onOpenChange, newBotRegistered }: Props): ReactNode {
  const [label, setLabel] = useState("");
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState<MintedEnrollCode | null>(null);
  const [, force] = useState(0);
  const [copiedField, setCopiedField] = useState<"code" | "line" | null>(null);

  // Tick once a second so the countdown stays fresh without a timer
  // elsewhere in the tree.
  useEffect(() => {
    if (!minted) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [minted]);

  // Reset state whenever the dialog closes so the next open starts clean.
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setMinted(null);
      setLabel("");
      setCopiedField(null);
    }, 200);
    return () => clearTimeout(t);
  }, [open]);

  // Auto-close on successful registration — the parent's polling spotted
  // a new bot row and flipped the signal.
  useEffect(() => {
    if (newBotRegistered && open) {
      toast.success("Bot Registered — Welcome To The Fleet");
      onOpenChange(false);
    }
  }, [newBotRegistered, open, onOpenChange]);

  const handleMint = async (): Promise<void> => {
    setMinting(true);
    try {
      const res = await mintEnrollCode(label.trim() || null);
      setMinted(res);
    } catch (e) {
      toast.error(`Couldn't mint code: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setMinting(false);
    }
  };

  const copy = async (field: "code" | "line", value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1800);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add A Bot Host</DialogTitle>
          <DialogDescription>
            Mint A One-Time Enroll Code, Paste The Installer On Your VPS, And The Bot Registers Itself.
          </DialogDescription>
        </DialogHeader>

        {minted ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
              <div className="mb-1 text-xs text-muted-foreground">Enroll Code</div>
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <code className="min-w-0 flex-1 truncate font-mono text-lg tracking-[0.2em] text-foreground">
                  {minted.code}
                </code>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Expires In <span className="font-mono">{countdown(minted.expiresAt)}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    onClick={() => void copy("code", minted.code)}
                    aria-label="Copy enroll code"
                  >
                    {copiedField === "code" ? <Check /> : <Copy />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Run This On The Host</span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => void copy("line", minted.oneLiner)}
                  aria-label="Copy one-liner"
                >
                  {copiedField === "line" ? <Check /> : <Copy />}
                </Button>
              </div>
              <pre className="overflow-x-auto rounded bg-background/60 p-3 font-mono text-xs leading-relaxed text-foreground">
                {minted.oneLiner}
              </pre>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin text-accent" />
              Waiting For The Bot To Register…
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs text-muted-foreground">
                Label <span className="text-muted-foreground/60">(Optional — E.g. "Pi Living Room")</span>
              </span>
              <input
                type="text"
                maxLength={64}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="pi-living-room"
                className="h-8 rounded-lg border border-border bg-background px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={minting}>
                Cancel
              </Button>
              <Button onClick={() => void handleMint()} disabled={minting}>
                {minting ? (
                  <>
                    <Loader2 className="animate-spin" /> Minting…
                  </>
                ) : (
                  "Mint Enroll Code"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
